import { useNavigate } from "react-router-dom";
import { FiFileText, FiTrash2 } from "react-icons/fi";
import { useFileService } from "../../api/fileService";
import { useState, useRef, useEffect, useMemo } from "react";
import { ContractType } from "@quantizr-engineering/common/types/show";
import { AiOutlineSearch } from "react-icons/ai";
import { prettyDate } from "../../utils/prettyDate";
import { removePdfExtension } from "../../utils/removePdfExtension";
import EllipsisMenu from "../../components/EllipsisMenu";
import { toast } from "sonner";
import { useCreedInsuranceContractService } from "../../api/insuranceContractService";
import { useCreedRecordLabelAgreementService } from "../../api/recordLabelService";

export default function Documents() {
  const { fileEntities } = useFileService();
  const navigate = useNavigate();

  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterText, setFilterText] = useState("");
  const dropdownRef = useRef(null);
  const headerRef = useRef(null);
  const { deleteFile } = useFileService();

  //TODO: Get a better solution for documentDetails to wait until things are loaded so that we don't get "no data found..."
  const { creedInsuranceContracts } = useCreedInsuranceContractService();
  const { creedRecordLabelAgreements } = useCreedRecordLabelAgreementService();

  const handleRowClick = (id: number) => {
    navigate(`/documents/${id}`);
  };

  const filteredFiles =
    fileEntities &&
    fileEntities.filter((file) => {
      const matchesType =
        selectedTypes.length === 0 || selectedTypes.includes(file.contractType);
      const matchesSearchTerm = removePdfExtension(file.filename)
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesType && matchesSearchTerm;
    });

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const removeType = (type: string) => {
    setSelectedTypes((prev) => prev.filter((t) => t !== type));
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      headerRef.current &&
      !(dropdownRef.current as HTMLElement).contains(event.target as Node) &&
      !(headerRef.current as HTMLElement).contains(event.target as Node)
    ) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      if (!dropdownOpen) {
        document.removeEventListener("mousedown", handleClickOutside);
      }
    };
  }, [dropdownOpen]);

  const handleSearch = (e: any) => {
    e.preventDefault();
    setSearchTerm(searchTerm);
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      handleSearch(e);
    }
  };

  const uniqueContractTypes = useMemo(() => {
    // const allValues = Object.values(ContractType);
    // return Array.from(new Set(allValues));
    // For now, limit types to the types we support
    return [
      ContractType.SHOW_OFFER,
      ContractType.LIVE_MUSIC_AGREEMENT,
      ContractType.LIVE_MUSIC_SETTLEMENT,
      ContractType.RECORDING_COLLABORATION_AGREEMENT,
      ContractType.BRAND_DEAL,
      ContractType.INSURANCE_CONTRACT,
      ContractType.RECORDING_RECORD_LABEL_AGREEMENT,
    ];
  }, [ContractType]);

  const filteredContractTypes = uniqueContractTypes.filter((type) =>
    type.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <div className="p-6 relative bg-gray-100 h-screen">
      <div className="flex items-center mb-4">
        <form onSubmit={handleSearch} className="relative w-full lg:w-1/2">
          <input
            type="text"
            placeholder="Search by document name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full px-4 py-2 text-gray-900 bg-white border border-gray-300 rounded-full focus:outline-none focus:border-purple-500"
          />
          <button
            type="submit"
            className="absolute right-3 top-3 text-gray-400"
          >
            <AiOutlineSearch />
          </button>
        </form>
      </div>

      <div className="flex items-center space-x-4 mb-6 relative">
        <button
          className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded inline-flex items-center"
          type="button"
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          Filter by Type
        </button>

        <div className="flex space-x-2">
          {selectedTypes.map((type) => (
            <div
              key={type}
              className="flex items-center bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full"
            >
              {type}
              <button
                className="ml-2 text-red-500 hover:text-red-700"
                onClick={() => removeType(type)}
              >
                &times;
              </button>
            </div>
          ))}
        </div>

        {dropdownOpen && (
          <div
            className="absolute mt-2 bg-gray-100 shadow-lg rounded-lg z-10 w-auto p-4 max-h-60 overflow-y-auto"
            ref={dropdownRef}
          >
            <input
              type="text"
              placeholder="Filter types..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded mb-2"
            />
            <ul className="grid grid-cols-1 gap-4">
              {filteredContractTypes.map((type) => (
                <li key={type}>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-blue-600 rounded"
                      checked={selectedTypes.includes(type)}
                      onChange={() => toggleType(type)}
                    />
                    <span className="ml-2 text-sm">{type}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="overflow-y-auto h-[calc(100%-3rem)] pb-48">
        <table className="min-w-full divide-y divide-gray-200 ">
          <thead className="sticky top-0 bg-gray-50">
            <tr>
              <th className="pl-6 py-3 text-left text-xs w-1/4 font-medium text-gray-500 uppercase tracking-wider">
                Document Name
              </th>
              <th
                className=" py-3 text-left text-xs font-medium w-1/6 text-gray-500 uppercase tracking-wider hover:bg-gray-200 cursor-pointer"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                ref={headerRef}
              >
                Type
              </th>
              <th className="py-3 text-left text-xs font-medium w-1/6 text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className=" py-3 text-left text-xs font-medium w-1/6 text-gray-500 uppercase tracking-wider">
                Uploaded at
              </th>
              <th className="w-1/4"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredFiles && filteredFiles.length > 0 ? (
              filteredFiles.map((fEntity) => (
                <tr
                  key={fEntity.id}
                  onClick={() => handleRowClick(fEntity.id as number)}
                  className="hover:bg-gray-100 cursor-pointer"
                >
                  <td className="text-sm font-medium text-gray-900 px-1 py-4 whitespace-nowrap truncate w-1/4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <FiFileText className="w-full h-full" />
                      </div>
                      {removePdfExtension(fEntity.filename)}
                    </div>
                  </td>
                  <td className="px-1 py-4 whitespace-nowrap">
                    {fEntity.contractType}
                  </td>
                  <td className="px-1 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        fEntity.status === "Processed"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {fEntity.status}
                    </span>
                  </td>
                  <td className="px-1 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fEntity.uploadedAt
                      ? `${prettyDate(fEntity.uploadedAt).day} ${
                          prettyDate(fEntity.uploadedAt).month
                        }`
                      : ""}
                  </td>
                  <td>
                    <div className="flex justify-end pr-16">
                      <EllipsisMenu
                        items={[
                          {
                            text: "Delete File",
                            onClick: (e) => {
                              e.stopPropagation();
                              toast("Delete this File?", {
                                duration: 10000,
                                action: {
                                  label: "Confirm",
                                  onClick: async () => {
                                    await deleteFile(fEntity.id);
                                  },
                                },
                                cancel: {
                                  label: "Cancel",
                                  onClick: () => {},
                                },
                              });
                            },
                            icon: <FiTrash2 />,
                          },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                  No documents match the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
